// `GET /auth/roles`: the permission sets this workspace answers to (#258).
//
// Read off `control_plane/routes/auth.py::list_roles` and the service under it
// (`auth/roles.py::Roles.list`), which merges the system four with the workspace's
// own custom rows and sorts them by rank. Every field below is one that
// `system_roles()` or `public_role()` puts in the body; `created_at` and
// `updated_at` are on custom rows only, which is why they are optional here.
//
// **Why the settings surface reads this one and not a settings table.** There
// is no workspace-settings route to read yet: the remediation policy is C3's, the
// integration connections are C7's, the standing egress grants are C6's, and
// each arrives with its unit. Roles is the workspace-scoped setting that already
// exists, and it is the one C4 will key its approver set off — "the holder
// side's active members whose role carries the approve permission". So the
// screen renders the catalogue that decision will read, rather than a page of
// facts the rail already shows.
//
// **A workspace is required.** `Roles.list` raises `AccessDenied` for a principal
// with no `workspace_id`, so an org-context session gets a refusal rather than an
// empty list, and the screen says which it got.

import type { Api } from "./http";

export const ROLES_PATH = "/auth/roles";

/** One row of `GET /auth/roles`: a system role or one of the workspace's own. */
export interface Role {
  role_id: string;
  name: string;
  permissions: string[];
  /** `ROLE_RANK` for a system role, 1..99 for a custom one. */
  level: number;
  /** False for the system four, which are never rows. */
  custom: boolean;
}

function asRole(value: unknown): Role | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.role_id !== "string") return null;
  return {
    role_id: row.role_id,
    name: typeof row.name === "string" ? row.name : row.role_id,
    permissions: Array.isArray(row.permissions)
      ? row.permissions.filter((p): p is string => typeof p === "string")
      : [],
    level: typeof row.level === "number" ? row.level : 0,
    custom: row.custom === true,
  };
}

export function rolesFrom(body: unknown): Role[] {
  if (!Array.isArray(body)) return [];
  const roles: Role[] = [];
  for (const value of body) {
    const role = asRole(value);
    if (role !== null) roles.push(role);
  }
  return roles;
}

export const listRoles = async (api: Api): Promise<Role[]> =>
  rolesFrom(await api.get<unknown>(ROLES_PATH));

/**
 * Whether the role a session holds carries a permission, by the server's own wildcard
 * rule: a granted `a:*` answers a requested `a:b`, and the segment counts must match
 * (`auth/permissions.py::permission_matches`).
 *
 * **Three states hold nothing, and each answers `false` on purpose** (#626), so a screen
 * in any of them offers no write:
 *
 * - roles that could not be read (`null`);
 * - a session holding no role at all (`roleId` null);
 * - a role id the catalogue does not hold — a real state, since a role can be deleted
 *   while someone is signed in with it. The console never guesses a stand-in role.
 *
 * The server enforces every permission this answers, so a wrong `true` would only offer
 * a control the API refuses; `false` is the answer that cannot mislead.
 */
export function roleHolds(roles: Role[] | null, roleId: string | null, permission: string): boolean {
  if (roles === null || roleId === null) return false;
  const role = roles.find((item) => item.role_id === roleId);
  if (role === undefined) return false;
  const requested = permission.split(":");
  return role.permissions.some((granted) => {
    const parts = granted.split(":");
    return parts.length === requested.length && parts.every((part, i) => part === "*" || part === requested[i]);
  });
}
